import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Container, Dropdown, Spinner } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isSampleLocalMode } from '../services/systems/sample-local/sampleLocalMode';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import SampleFormModal from '../components/samples/SampleFormModal';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import ReportDetailedFilterFields from '../components/samples/ReportDetailedFilterFields';
import BaseDateField, {
  BASE_DATE_FIELD_COMPACT_WIDTH,
  formatIsoDateForDisplay,
} from '../components/samples/BaseDateField';
import KeywordDetailedFilterBar from '../components/samples/KeywordDetailedFilterBar';
import UsageRecordsDayTable from '../components/samples/UsageRecordsDayTable';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import SampleListChromeTools, {
  ToolsMenuHelp,
} from '../components/samples/SampleListChromeTools';
import SampleNeedsStorageSetupAlert from '../components/samples/SampleNeedsStorageSetupAlert';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { useSampleStorageSetupGate } from '../hooks/useSampleStorageSetupGate';
import { useMdUp } from '../hooks/useLaboSukeMdUp';
import { useListFirstDetailedFilterOnMount, usePersistedBoolean } from '../hooks/usePersistedBoolean';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useSampleEditLoader } from '../hooks/useSampleEditLoader';
import { sampleService } from '../services/sample.service';
import { translateAxiosErrorMessage } from '../utils/error.utils';
import { deliverBlobToUser } from '../utils/downloadBlob';
import { navigateToSampleDetail } from '../utils/samplePageNavigation';
import { formatEntityDisplayName } from '../utils/softBreakIdentifier';
import {
  reportService,
  type ReportFilters,
  type UsageRecordDayRow,
} from '../services/report.service';
import {
  USAGE_RECORDS_DETAILED_FILTER_STORAGE_KEY,
  countDetailedReportFilters,
  normalizeReportFilters,
} from '../utils/reportFilters';
import { nextExpandedDaysOnToggleAll } from '../utils/usageRecordsDayDisplay';

/** 初期は最新日だけ開く（長いサンプル名で画面が埋まるのを防ぐ） */
const AUTO_EXPAND_DAY_COUNT = 1;

function toggleExpandedDay(date: string, expandedDays: Set<string>): Set<string> {
  const next = new Set(expandedDays);
  if (next.has(date)) {
    next.delete(date);
  } else {
    next.add(date);
  }
  return next;
}

export default function UsageRecords() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(isAdmin);
  const storageSetupGate = useSampleStorageSetupGate(adminViewAll);
  const isMdUp = useMdUp();
  const queryClient = useQueryClient();

  const [baseDate, setBaseDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [filters, setFilters] = useState<ReportFilters>({});
  const debouncedFilters = useDebouncedValue(filters, 300);
  const [detailedFilterOpen, setDetailedFilterOpen, toggleDetailedFilter] = usePersistedBoolean(
    USAGE_RECORDS_DETAILED_FILTER_STORAGE_KEY,
    false
  );
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportError, setExportError] = useState('');
  const [pageError, setPageError] = useState('');
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => new Set());

  const {
    selectedSample,
    showEditModal,
    openEditById,
    closeEdit,
  } = useSampleEditLoader({
    userId: user?.id,
    isAdmin,
    adminViewAll,
    t,
    onError: setPageError,
    logContext: 'usage records edit',
  });

  /** 入力直後の連打リクエストを避けつつ、レポート同様に「適用ボタン」なしで反映 */
  const normalizedFilters = useMemo(
    () => normalizeReportFilters(debouncedFilters),
    [debouncedFilters]
  );
  const detailedFilterCount = useMemo(() => countDetailedReportFilters(filters), [filters]);
  useListFirstDetailedFilterOnMount(
    setDetailedFilterOpen,
    detailedFilterCount,
    USAGE_RECORDS_DETAILED_FILTER_STORAGE_KEY
  );

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof sampleService.update>[1] }) =>
      sampleService.update(id, data, {
        adminViewAll: adminViewAll || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage-records-by-date'] });
      closeEdit();
    },
    onError: (err) => {
      setPageError(
        translateAxiosErrorMessage(err, t, t('sample.update_failed'))
      );
    },
  });

  const handleOpenSample = (sampleId: number) => {
    navigateToSampleDetail(navigate, sampleId, {
      preferAdminViewAll: adminViewAll || undefined,
      returnPath: '/samples/usage-records',
    });
  };

  const handleToggleDay = (date: string) => {
    setExpandedDays((prev) => toggleExpandedDay(date, prev));
  };

  const renderEditCell = (row: UsageRecordDayRow) => {
    const nameLabel = formatEntityDisplayName(row.sampleName, t);
    const editTitle = t('usage_records.edit_sample_aria', {
      name: nameLabel,
      defaultValue: `${t('common.edit')} · ${nameLabel}`,
    });
    return (
      <div className="text-center">
        <Button
          variant="link"
          size="sm"
          className="p-1 text-secondary"
          title={editTitle}
          aria-label={t('common.edit')}
          onClick={() => {
            void openEditById(row.sampleId);
          }}
        >
          <i className="fas fa-edit" aria-hidden="true" />
        </Button>
      </div>
    );
  };

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['usage-records-by-date', baseDate, normalizedFilters, adminViewAll],
    queryFn: () =>
      reportService.getUsageRecordsByDate({
        baseDate,
        filters: normalizedFilters,
        adminViewAll: adminViewAll || undefined,
      }),
  });

  const daysSignature = data?.days?.map((d) => d.date).join(',') ?? '';

  useEffect(() => {
    if (!daysSignature) {
      setExpandedDays(new Set());
      return;
    }
    const dates = daysSignature.split(',');
    setExpandedDays(new Set(dates.slice(0, AUTO_EXPAND_DAY_COUNT)));
    // 日セット／基準日が変わったときだけ自動展開。キーワード入力中は展開状態を維持する。
  }, [baseDate, daysSignature]);

  const dayCount = data?.days?.length ?? 0;
  /** 1日でも開いていれば「折りたたみ」側（初期の最新日だけ展開にも対応） */
  const showCollapseAll = expandedDays.size > 0;

  const toggleAllDays = () => {
    if (!data?.days?.length) return;
    setExpandedDays(
      nextExpandedDaysOnToggleAll(
        data.days.map((d) => d.date),
        expandedDays
      )
    );
  };

  const clearAllFilters = () => {
    setFilters({});
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    setExportError('');
    try {
      const blob = await reportService.exportUsageRecordsPdf({
        baseDate,
        filters: normalizedFilters,
        adminViewAll: adminViewAll || undefined,
      });
      deliverBlobToUser(blob, `usage_records_${baseDate}.pdf`);
    } catch {
      setExportError(t('usage_records.export_failed'));
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    setExportError('');
    try {
      const blob = await reportService.exportUsageRecordsExcel({
        baseDate,
        filters: normalizedFilters,
        adminViewAll: adminViewAll || undefined,
      });
      deliverBlobToUser(blob, `usage_records_${baseDate}.xlsx`);
    } catch {
      setExportError(t('usage_records.export_failed'));
    } finally {
      setExportingExcel(false);
    }
  };

  /** 読込中はメニュー全体を止めない（詳細フィルター／ヘルプを開ける）。書き出し中のみ busy */
  const pdfBusy = exportingPdf;
  const excelBusy = exportingExcel;

  return (
    <Container fluid className="mt-2 px-3" id="usage-records-page">
      <SampleManagerNavigation />

      {/* ナビ短名「履歴」と可視 h1 が縦を奪うため sr-only。ヘルプは filter 帯、管理者は md+ ここ / xs は meta 帯 */}
      <SampleManagerPageHeading
        shortLabel={t('navigation.usage_records_short', { defaultValue: 'Usage' })}
        fullLabel={t('usage_records.title')}
        iconClassName="fas fa-history"
        srOnly
      />
      <p className="visually-hidden">
        {data?.periodStart && data?.periodEnd
          ? t('usage_records.period_range', {
              start: formatIsoDateForDisplay(data.periodStart),
              end: formatIsoDateForDisplay(data.periodEnd),
              defaultValue: '{{start}}〜{{end}}（過去1年）',
            })
          : t('usage_records.subtitle')}
      </p>
      {storageSetupGate.isReady && !storageSetupGate.hasStorageRooms ? (
        <SampleNeedsStorageSetupAlert />
      ) : null}
      {showAdminViewToggle && isMdUp ? (
        <div className="d-flex justify-content-end mb-2">
          <AdminStorageViewToggle
            checked={adminViewAll}
            onChange={setAdminViewAll}
            inputId="usage-records-admin-view-all"
            dense
            className="flex-shrink-0"
            labelKey="usage_records.admin_view_all_label"
            shortLabelKey="usage_records.admin_view_all_label_short"
            offShortLabelKey="usage_records.admin_view_scope_mine_short"
            hintKey="usage_records.admin_view_all_hint"
          />
        </div>
      ) : null}

      {!showAdminViewToggle ? (
        <Alert variant="info" className="mb-2 py-2 small">
          <i className="fas fa-info-circle me-1" aria-hidden="true" />
          {t('usage_records.user_only_note')}
        </Alert>
      ) : null}

      {exportError && (
        <Alert variant="danger" className="mb-2" onClose={() => setExportError('')} dismissible>
          {exportError}
        </Alert>
      )}

      {pageError && (
        <Alert variant="danger" className="mb-2" onClose={() => setPageError('')} dismissible>
          {pageError}
        </Alert>
      )}

      <KeywordDetailedFilterBar
        sticky
        keywordId="usage-records-filter-keyword"
        keyword={filters.keyword || ''}
        onKeywordChange={(value) =>
          setFilters({ ...filters, keyword: value || undefined })
        }
        keywordPlaceholder={t('usage_records.filter_live_hint_short')}
        keywordAriaLabel={t('usage_records.filter_keyword_aria', {
          defaultValue: '履歴検索',
        })}
        keywordTitle={t('usage_records.filter_live_hint')}
        hideKeywordLabel
        detailedOpen={detailedFilterOpen}
        detailedCount={detailedFilterCount}
        onToggleDetailed={toggleDetailedFilter}
        onClear={clearAllFilters}
        leading={
          <BaseDateField
            id="usage-records-base-date"
            label={t('reports.base_date')}
            value={baseDate}
            max={format(new Date(), 'yyyy-MM-dd')}
            lang={i18n.language}
            title={t('usage_records.base_date_note_short')}
            width={BASE_DATE_FIELD_COMPACT_WIDTH}
            hideLabel
            onChange={setBaseDate}
            className="usage-records-base-date"
          />
        }
        trailing={
          <SampleListChromeTools
            toggleId="usage-records-export-toggle"
            toggleClassName="usage-records-export-toggle"
            busy={pdfBusy || excelBusy}
            compactChrome
            showExportSlot={false}
            help={
              <CollapsibleHelpSection
                compact
                expandedPortalId="usage-records-help-expanded"
                titleKey="usage_records.how_to_use_title"
                descriptionKey="usage_records.how_to_use_desc"
                stepsKey={[
                  'usage_records.how_to_use_step1',
                  'usage_records.how_to_use_step2',
                  'usage_records.how_to_use_step3',
                ]}
                storageKey="usage_records"
              />
            }
            menu={
              <>
                <ToolsMenuHelp pageSelector="#usage-records-page" />
                {!isSampleLocalMode() ? (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      disabled={excelBusy}
                      title={t('usage_records.export_excel_title', {
                        defaultValue: '使用履歴をExcelでダウンロード',
                      })}
                      onClick={() => {
                        void handleExportExcel();
                      }}
                    >
                      <i className="fas fa-file-excel me-2 text-success" aria-hidden="true" />
                      {t('usage_records.export_excel_short', { defaultValue: 'Excel' })}
                    </Dropdown.Item>
                  </>
                ) : null}
              </>
            }
            afterTools={
              !isSampleLocalMode() ? (
              <>
                {/* PDF は主操作（モバイルでも ⋯ に埋めない）。Excel は md+ のみ常時 */}
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="sample-chrome-icon-btn usage-records-pdf-chrome-btn flex-shrink-0"
                  disabled={pdfBusy}
                  onClick={() => {
                    void handleExportPdf();
                  }}
                  title={t('usage_records.export_pdf_title', {
                    defaultValue: '使用履歴をPDFでダウンロード',
                  })}
                  aria-label={t('usage_records.export_pdf_short', {
                    defaultValue: 'PDF',
                  })}
                >
                  {exportingPdf ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fas fa-file-pdf" aria-hidden="true" />
                  )}
                </Button>
                {/* sample-chrome-icon-btn の display:!important を避けるため親で表示制御 */}
                <span className="d-none d-md-inline-flex flex-shrink-0">
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="sample-chrome-icon-btn flex-shrink-0"
                    disabled={excelBusy}
                    onClick={() => {
                      void handleExportExcel();
                    }}
                    title={t('usage_records.export_excel_title', {
                      defaultValue: '使用履歴をExcelでダウンロード',
                    })}
                    aria-label={t('usage_records.export_excel_short', {
                      defaultValue: 'Excel',
                    })}
                  >
                    {exportingExcel ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className="fas fa-file-excel" aria-hidden="true" />
                    )}
                  </Button>
                </span>
              </>
              ) : null
            }
          />
        }
        detailedPanel={
          <ReportDetailedFilterFields
            idPrefix="usage-records"
            filters={filters}
            onFiltersChange={setFilters}
            isAdmin={isAdmin}
            showCreatorFilter={adminViewAll}
            size="sm"
          />
        }
      />

      <div id="usage-records-help-expanded" />

      {isLoading && (
        <div className="text-center mb-3">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      )}

      {isError && (
        <Alert variant="danger">
          {t('usage_records.load_failed')}
          <Button variant="link" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      )}

      {data && !isLoading && (
        <div style={{ opacity: isFetching ? 0.65 : 1, transition: 'opacity 0.15s ease' }}>
          {data.days.length === 0 ? (
            <div className="rs-empty-state py-4" role="status">
              <div className="rs-empty-state__icon" aria-hidden="true">
                <i className="fas fa-clipboard-list" />
              </div>
              <p className="rs-empty-state__text mb-0">{t('usage_records.no_records')}</p>
            </div>
          ) : (
            <UsageRecordsDayTable
              days={data.days}
              expandedDays={expandedDays}
              onToggleDay={handleToggleDay}
              onOpenSample={handleOpenSample}
              renderEditCell={renderEditCell}
              headerTrailing={
                <div
                  className="d-flex align-items-center gap-1 flex-wrap justify-content-end usage-records-meta-toolbar"
                  aria-live="polite"
                >
                  {showAdminViewToggle && !isMdUp ? (
                    <AdminStorageViewToggle
                      checked={adminViewAll}
                      onChange={setAdminViewAll}
                      inputId="usage-records-admin-view-all-mobile"
                      compact
                      className="flex-shrink-0"
                      labelKey="usage_records.admin_view_all_label"
                      shortLabelKey="usage_records.admin_view_all_label_short"
                      offShortLabelKey="usage_records.admin_view_scope_mine_short"
                      hintKey="usage_records.admin_view_all_hint"
                    />
                  ) : null}
                  <Badge
                    bg="secondary"
                    className="usage-records-meta-toolbar__badge"
                    title={t('usage_records.total_records', { count: data.totalRecords })}
                  >
                    {t('usage_records.total_records_short', {
                      count: data.totalRecords,
                      defaultValue: String(data.totalRecords),
                    })}
                  </Badge>
                  <Badge
                    bg="light"
                    text="dark"
                    className="usage-records-meta-toolbar__badge"
                    title={t('usage_records.day_count', { count: data.days.length })}
                  >
                    {t('usage_records.day_count_short', {
                      count: data.days.length,
                      defaultValue: String(data.days.length),
                    })}
                  </Badge>
                  {isFetching ? (
                    <Spinner animation="border" size="sm" role="status">
                      <span className="visually-hidden">{t('common.loading')}</span>
                    </Spinner>
                  ) : null}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="sample-chrome-icon-btn usage-records-day-toggle__btn"
                    onClick={toggleAllDays}
                    disabled={dayCount === 0}
                    title={
                      showCollapseAll
                        ? t('usage_records.collapse_all')
                        : t('usage_records.expand_all')
                    }
                    aria-label={
                      showCollapseAll
                        ? t('usage_records.collapse_all_short', {
                            defaultValue: t('usage_records.collapse_all'),
                          })
                        : t('usage_records.expand_all_short', {
                            defaultValue: t('usage_records.expand_all'),
                          })
                    }
                    aria-pressed={showCollapseAll}
                  >
                    <i
                      className={`fas ${showCollapseAll ? 'fa-angles-up' : 'fa-angles-down'}`}
                      aria-hidden="true"
                    />
                  </Button>
                </div>
              }
            />
          )}
        </div>
      )}

      {showEditModal && selectedSample ? (
        <SampleFormModal
          show
          onHide={closeEdit}
          onSubmit={async (data) => {
            await updateMutation.mutateAsync({ id: selectedSample.id, data });
          }}
          initialData={selectedSample}
          isLoading={updateMutation.isPending}
          adminStorageViewAll={adminViewAll}
        />
      ) : null}
    </Container>
  );
}
